import * as React from 'react'

import { Section } from '@factorialco/deprecated-design-system'
import { Features } from 'generated/featureDefinitions'

import { observer } from 'mobx-react'

import InformationTooltip from 'modules/core/components/Inputs/InformationTooltip'
import Select from 'modules/core/components/Inputs/Select'
import { type FeatureAvailabilityType } from 'modules/core/hooks/useFeatureAvailability'
import i18n from 'modules/core/lib/i18n'
import { isFeatureAvailabilityEnabled } from 'modules/core/lib/isFeatureAvailabilityEnabled'

import type AllowanceForm from 'modules/timeoff/lib/AllowanceForm'
import {
  limitNegativeCounterOptionsLabel,
  negativeCounterDaysOptionsLabel,
  negativeCounterHoursOptionsLabel,
  timeoffCycleOptionsLabel,
} from 'modules/timeoff/lib/dynamicTranslations'
import Allowance, { TIMEOFF_CYCLE } from 'modules/timeoff/store/models/Allowance'

import AllowanceTypeAndRoundingSelect from './_AllowanceTypeAndRoundingSelect'
import BaseOnFixedBalance from './_BaseOnFixedBalance'
import BaseOnTimeWorked from './_BaseOnTimeWorked'

interface Props {
  allowance: Allowance
  allowanceForm: AllowanceForm
  isHourly: boolean
  featureAvailabilities: FeatureAvailabilityType[]
  isTimeWorked: boolean
  isOvertime: boolean
  // TODO: Remove countSmRestDaysOptionAvailable once Feature Flag is not needed
  countSmRestDaysOptionAvailable: boolean
}

const AllowanceSettings = ({
  allowance,
  allowanceForm,
  isHourly,
  featureAvailabilities,
  isTimeWorked,
  isOvertime,
  // TODO: Remove countSmRestDaysOptionAvailable once Feature Flag is not needed
  countSmRestDaysOptionAvailable,
}: Props) => {
  // TODO: deprecate this cycleOptions when flexible cycles are launched
  const cycleOptions = TIMEOFF_CYCLE.map((value) => ({
    label: timeoffCycleOptionsLabel(value),
    value,
  }))

  const flexibleCyclesEnabled = isFeatureAvailabilityEnabled(
    featureAvailabilities,
    Features.timeoff_flexible_cycles
  )

  const accruedUnitsAvailabilityOptions = Object.keys(Allowance.ACCRUED_UNITS_AVAILABILITY).map(
    (value) => ({
      label: allowance.accruedUnitsAvailabilityOptions(value),
      value,
    })
  )

  const negativeCounterDaysOptions = Object.keys(Allowance.NEGATIVE_COUNTER_TYPE).map((value) => ({
    label: negativeCounterDaysOptionsLabel(value),
    value,
  }))

  const negativeCounterHoursOptions = Object.keys(Allowance.NEGATIVE_COUNTER_TYPE).map((value) => ({
    label: negativeCounterHoursOptionsLabel(value),
    value,
  }))

  const negativeCounterOptions = isHourly ? negativeCounterHoursOptions : negativeCounterDaysOptions

  const limitNegativeCounterOptions = React.useMemo(() => {
    const options = Object.keys(Allowance.LIMIT_NEGATIVE_COUNTER_TYPE).map((value) => ({
      label: limitNegativeCounterOptionsLabel(value),
      value,
    }))

    if (
      allowanceForm.get('negative_counter_type').value ===
      Allowance.NEGATIVE_COUNTER_TYPE.negative_counter_disabled
    ) {
      options.unshift({
        label: '-',
        value: '' as const,
      })
    }

    return options
  }, [allowanceForm.get('negative_counter_type').value])

  const renderNegativeCounter = isFeatureAvailabilityEnabled(
    featureAvailabilities,
    Features.timeoff_allowance_negative_counter
  )

  const accruedUnitsAvailabilityLabel = i18n.t(
    'time_off.allowances.page.labels.accrued_units_availability'
  )

  const accruedUnitsAvailabilityDesc = i18n.t(
    'time_off.allowances.page.information.accrued_units_availability'
  )

  const negativeCounterLabel = i18n.t('time_off.allowances.page.labels.negative_counter_type')

  const limitNegativeCounterLabel = i18n.t(
    'time_off.allowances.page.labels.limit_negative_counter_type'
  )

  const renderLimitNegativeCounter =
    allowanceForm.get('negative_counter_type').value ===
    Allowance.NEGATIVE_COUNTER_TYPE.negative_counter_enabled

  React.useEffect(() => {
    const negativeCounterField = allowanceForm.get('negative_counter_type')
    const limitNegativeCounterField = allowanceForm.get('limit_negative_counter_type')

    if (negativeCounterField.value === Allowance.NEGATIVE_COUNTER_TYPE.negative_counter_disabled) {
      limitNegativeCounterField.set('')
    } else if (!limitNegativeCounterField.value) {
      limitNegativeCounterField.set(Allowance.LIMIT_NEGATIVE_COUNTER_TYPE.no_limit)
    }
  }, [allowanceForm.get('negative_counter_type').value])

  return (
    <Section.Form
      illustration="holidays-event-date"
      title={i18n.t('time_off.allowances.page.allowance_settings.title')}
      description={i18n.t('time_off.allowances.page.allowance_settings.description')}
      submitLabel={i18n.t('common.submit')}
    >
      <AllowanceTypeAndRoundingSelect
        allowance={allowance}
        allowanceForm={allowanceForm}
        isHourly={isHourly}
        isOvertime={isOvertime}
        isTimeWorked={isTimeWorked}
      />
      {!isOvertime && (
        <>
          {isTimeWorked && (
            <BaseOnTimeWorked
              allowanceForm={allowanceForm}
              featureAvailabilities={featureAvailabilities}
            />
          )}

          {!isTimeWorked && (
            <BaseOnFixedBalance
              allowanceForm={allowanceForm}
              isHourly={isHourly}
              featureAvailabilities={featureAvailabilities}
              // TODO: Remove countSmRestDaysOptionAvailable once Feature Flag is not needed
              countSmRestDaysOptionAvailable={countSmRestDaysOptionAvailable}
            />
          )}
          <Select
            field={allowanceForm.get('accrued_units_availability')}
            label={accruedUnitsAvailabilityLabel}
            secondaryLabel={
              <InformationTooltip information={accruedUnitsAvailabilityDesc} icon="question" />
            }
            options={accruedUnitsAvailabilityOptions}
            readonly={!allowance.isNew}
            extra={{
              'data-intercom-target': 'timeoff-allowances-accrued-units-availability-input',
            }}
          />
          {renderNegativeCounter && (
            <Select
              label={negativeCounterLabel}
              field={allowanceForm.get('negative_counter_type')}
              options={negativeCounterOptions}
              extra={{
                'data-intercom-target': 'timeoff-allowances-negative-counter-type-input',
              }}
            />
          )}
          {renderLimitNegativeCounter && (
            <Select
              label={limitNegativeCounterLabel}
              field={allowanceForm.get('limit_negative_counter_type')}
              options={limitNegativeCounterOptions}
            />
          )}
          {!flexibleCyclesEnabled && (
            <Select
              field={allowanceForm.get('timeoff_cycle')}
              label={i18n.t('time_off.allowances.page.labels.timeoff_cycle')}
              secondaryLabel={
                <InformationTooltip
                  information={i18n.t('time_off.allowances.page.information.timeoff_cycle')}
                  icon="question"
                />
              }
              options={cycleOptions}
              readonly={!allowance.isNew}
              showOriginalLabelText
            />
          )}
        </>
      )}
      {isOvertime && (
        <>
          {renderNegativeCounter && (
            <Select
              label={negativeCounterLabel}
              field={allowanceForm.get('negative_counter_type')}
              options={negativeCounterOptions}
            />
          )}
        </>
      )}
    </Section.Form>
  )
}

export default observer(AllowanceSettings)
